// Comprehensive service data for all service pages

import { additionalServicesData } from './additionalServicesData';

export const allServicesData = {
  // TRADEMARK SERVICES
  'trademark-clearance': {
    title: 'Trademark Clearance Searches',
    category: 'Trademark Services',
    tagline: 'Verify your mark\'s availability before filing',
    description: 'Comprehensive trademark clearance searches to minimize risk and maximize success',
    buttonText: 'Get Clearance',
    hero: {
      title: 'Comprehensive Trademark Clearance',
      subtitle: 'Verify availability and reduce legal risks before filing your trademark application'
    },
    what: {
      title: 'What is Trademark Clearance Search?',
      content: [
        'A trademark clearance search is an in-depth investigation that goes beyond basic USPTO database searches to identify potential conflicts with existing trademarks. This comprehensive analysis examines federal and state registrations, common law usage, domain names, social media handles, and international marks that could pose obstacles to your trademark registration.',
        'Our clearance searches provide detailed legal analysis and risk assessment, helping you understand the likelihood of successful registration and potential opposition from existing mark holders. This critical step can save thousands of dollars in rebranding costs and legal disputes by identifying conflicts before you invest in filing.'
      ],
      quote: 'A thorough clearance search is the foundation of a successful trademark strategy, preventing costly conflicts and ensuring long-term brand protection.'
    },
    benefits: [
      { icon: 'Shield', title: 'Risk Mitigation', desc: 'Identify potential conflicts before filing and avoid costly legal disputes with existing mark holders.' },
      { icon: 'CheckCircle2', title: 'Higher Approval Rate', desc: 'Increase your chances of USPTO approval by clearing potential obstacles in advance.' },
      { icon: 'DollarSign', title: 'Cost Savings', desc: 'Avoid wasted filing fees and rebranding expenses by identifying issues early.' },
      { icon: 'FileText', title: 'Comprehensive Report', desc: 'Receive detailed analysis of similar marks with risk assessment and recommendations.' },
      { icon: 'Globe', title: 'International Coverage', desc: 'Optional searches in international databases for global brand protection.' },
      { icon: 'Scale', title: 'Legal Opinion', desc: 'Attorney-prepared analysis of search results with strategic guidance.' },
      { icon: 'Clock', title: 'Fast Turnaround', desc: 'Receive your comprehensive clearance report within 3-5 business days.' },
      { icon: 'Lock', title: 'Confidential', desc: 'All searches conducted with strict confidentiality and attorney-client privilege.' }
    ],
    pricing: [
      {
        name: 'Basic Clearance',
        price: '$399',
        description: 'Essential trademark clearance',
        features: [
          'USPTO Database Search',
          'Identical Mark Search',
          'Basic Conflict Report',
          'Email Delivery',
          '3-Day Turnaround'
        ]
      },
      {
        name: 'Comprehensive',
        price: '$799',
        description: 'Complete clearance analysis',
        popular: true,
        features: [
          'Everything in Basic',
          'Common Law Search',
          'State Registrations',
          'Domain Name Search',
          'Attorney Risk Analysis',
          'Strategic Recommendations',
          '48-Hour Rush Available'
        ]
      },
      {
        name: 'International',
        price: '$1,499',
        description: 'Global trademark clearance',
        features: [
          'Everything in Comprehensive',
          'International Database Search',
          'Madrid Protocol Countries',
          'Global Risk Assessment',
          'Multi-Jurisdiction Analysis',
          'Priority Support'
        ]
      }
    ],
    process: [
      {
        title: 'Consultation & Requirements',
        description: 'Share your proposed trademark, business details, and markets you plan to operate in. Our team gathers comprehensive information about your brand.'
      },
      {
        title: 'Multi-Database Search',
        description: 'We conduct thorough searches across USPTO, state registrations, common law sources, domain databases, and international records as selected.'
      },
      {
        title: 'Attorney Analysis & Report',
        description: 'Receive detailed clearance report with risk assessment, similar marks analysis, and strategic recommendations for proceeding.'
      }
    ]
  },

  'trademark-application': {
    title: 'Trademark Application Preparation',
    category: 'Trademark Services',
    tagline: 'Expert preparation of USPTO trademark applications',
    description: 'Professional trademark application preparation ensuring accuracy and compliance',
    buttonText: 'Prepare Application',
    hero: {
      title: 'Expert Trademark Application Preparation',
      subtitle: 'Professionally prepared USPTO applications with proper classification and description'
    },
    what: {
      title: 'What is Trademark Application Preparation?',
      content: [
        'Trademark application preparation is the critical process of drafting and filing your trademark application with the United States Patent and Trademark Office (USPTO). This complex process requires precise identification of goods and services, proper classification selection, accurate mark description, and strategic specimen submission.',
        'A properly prepared application is essential for successful registration. Errors in classification, inadequate descriptions, or improper specimens can lead to office actions, delays, or outright rejection. Our experienced trademark attorneys ensure every detail is correct from the start, maximizing your chances of smooth approval.'
      ],
      quote: 'Proper application preparation is the difference between smooth sailing and costly delays in your trademark journey.'
    },
    benefits: [
      { icon: 'CheckCircle2', title: 'Accurate Classification', desc: 'Proper selection of international trademark classes for comprehensive protection.' },
      { icon: 'FileText', title: 'Precise Descriptions', desc: 'Attorney-drafted descriptions that meet USPTO standards and maximize protection scope.' },
      { icon: 'Shield', title: 'Specimen Strategy', desc: 'Expert guidance on specimen selection to demonstrate proper trademark use in commerce.' },
      { icon: 'Scale', title: 'Legal Compliance', desc: 'Full compliance with USPTO rules and trademark examination guidelines.' },
      { icon: 'Clock', title: 'Avoid Delays', desc: 'Reduce likelihood of office actions and refusal by getting it right the first time.' },
      { icon: 'Award', title: 'Stronger Protection', desc: 'Strategic application preparation for maximum trademark protection and enforcement.' },
      { icon: 'Target', title: 'Basis Selection', desc: 'Choose the right filing basis (use in commerce or intent-to-use) for your situation.' },
      { icon: 'Globe', title: 'International Options', desc: 'Guidance on international applications and Madrid Protocol filings.' }
    ],
    pricing: [
      {
        name: 'Standard Application',
        price: '$599',
        description: 'Single class trademark filing',
        features: [
          'Attorney-Prepared Application',
          'Single Class Coverage',
          'Goods/Services Description',
          'Proper Classification',
          'USPTO Filing Included',
          'Specimen Review'
        ]
      },
      {
        name: 'Multi-Class',
        price: '$999',
        description: 'Multiple class protection',
        popular: true,
        features: [
          'Everything in Standard',
          'Up to 3 Classes',
          'Comprehensive Description',
          'Strategic Classification',
          'Enhanced Protection',
          'Priority Review'
        ]
      },
      {
        name: 'Premium',
        price: '$1,799',
        description: 'Comprehensive filing strategy',
        features: [
          'Everything in Multi-Class',
          'Unlimited Classes',
          'Clearance Search Included',
          'Intent-to-Use Option',
          'International Guidance',
          'Dedicated Attorney Support'
        ]
      }
    ],
    process: [
      {
        title: 'Information Gathering',
        description: 'Collect details about your mark, business activities, goods/services, and usage information to determine optimal filing strategy.'
      },
      {
        title: 'Application Drafting',
        description: 'Attorney prepares comprehensive application with proper classification, precise descriptions, and strategic specimen selection.'
      },
      {
        title: 'Review and Filing',
        description: 'Final review with client approval, then immediate electronic filing with USPTO. Tracking number and confirmation provided.'
      }
    ]
  },

  'trademark-office-actions': {
    title: 'Trademark Office Actions',
    category: 'Trademark Services',
    tagline: 'Professional responses to USPTO objections',
    description: 'Expert handling of trademark office actions and examiner objections',
    buttonText: 'Respond Now',
    hero: {
      title: 'Trademark Office Action Response',
      subtitle: 'Expert legal responses to USPTO objections and refusals'
    },
    what: {
      title: 'What is a Trademark Office Action?',
      content: [
        'A trademark office action is an official letter from a USPTO examining attorney raising objections or requirements regarding your trademark application. Office actions can cite issues such as likelihood of confusion with existing marks, descriptiveness, specimen deficiencies, or classification errors. Responding properly within the 6-month deadline is crucial—failure to respond results in application abandonment.',
        'Office actions require sophisticated legal analysis and precise responses. Our experienced trademark attorneys analyze the objections, research relevant case law, prepare persuasive legal arguments, and submit comprehensive responses that address every examiner concern while protecting your trademark rights.'
      ],
      quote: 'A well-crafted office action response can overcome refusals and save applications that might otherwise be abandoned.'
    },
    benefits: [
      { icon: 'Scale', title: 'Legal Expertise', desc: 'Attorney-prepared responses with case law citations and persuasive legal arguments.' },
      { icon: 'Target', title: 'Strategic Analysis', desc: 'Comprehensive review of objections with multiple response strategies evaluated.' },
      { icon: 'CheckCircle2', title: 'High Success Rate', desc: 'Proven track record of overcoming office action refusals and objections.' },
      { icon: 'Clock', title: 'Timely Response', desc: 'Responses prepared and filed well before the 6-month USPTO deadline.' },
      { icon: 'FileText', title: 'Detailed Arguments', desc: 'Point-by-point responses addressing every examiner concern thoroughly.' },
      { icon: 'Shield', title: 'Specimen Correction', desc: 'Preparation and submission of corrected specimens when needed.' },
      { icon: 'Award', title: 'Amendment Strategy', desc: 'Strategic amendments to descriptions, disclaimers, or classifications.' },
      { icon: 'Phone', title: 'Examiner Interviews', desc: 'Direct communication with examining attorneys when beneficial.' }
    ],
    pricing: [
      {
        name: 'Simple Response',
        price: '$599',
        description: 'Minor technical corrections',
        features: [
          'Attorney Review',
          'Response Preparation',
          'Minor Amendments',
          'Specimen Correction',
          'USPTO Filing'
        ]
      },
      {
        name: 'Standard Response',
        price: '$999',
        description: 'Complex objection handling',
        popular: true,
        features: [
          'Everything in Simple',
          'Legal Research',
          'Case Law Arguments',
          'Likelihood of Confusion',
          'Descriptiveness Arguments',
          'Examiner Interview'
        ]
      },
      {
        name: 'Appeal Preparation',
        price: '$2,499',
        description: 'TTAB appeal filing',
        features: [
          'Everything in Standard',
          'Appeal Brief Preparation',
          'Evidence Compilation',
          'TTAB Filing',
          'Appellate Arguments',
          'Full Appeal Support'
        ]
      }
    ],
    process: [
      {
        title: 'Office Action Analysis',
        description: 'Comprehensive review of examiner objections, legal research on cited references, and evaluation of response strategies.'
      },
      {
        title: 'Response Preparation',
        description: 'Attorney drafts detailed response with legal arguments, case law citations, and any necessary amendments or corrected specimens.'
      },
      {
        title: 'Filing and Follow-Up',
        description: 'Response filed with USPTO, examiner interview conducted if needed, and continued monitoring until issue resolution.'
      }
    ]
  },

  'trademark-monitoring': {
    title: 'Trademark Monitoring',
    category: 'Trademark Services',
    tagline: 'Continuous surveillance of potential infringements',
    description: 'Proactive trademark monitoring to protect your brand from infringement',
    buttonText: 'Start Monitoring',
    hero: {
      title: 'Comprehensive Trademark Monitoring',
      subtitle: 'Proactive surveillance and protection against trademark infringement'
    },
    what: {
      title: 'What is Trademark Monitoring?',
      content: [
        'Trademark monitoring is the ongoing surveillance of new trademark applications, registrations, and commercial use to identify potential infringement of your trademark rights. This proactive service watches for confusingly similar marks being filed with the USPTO, used in commerce, registered as domain names, or appearing in online marketplaces.',
        'Early detection is critical for trademark enforcement. The sooner you identify a potential infringement, the easier and less expensive it is to resolve. Our monitoring service provides regular reports on potentially conflicting marks and strategic guidance on appropriate enforcement actions, from cease and desist letters to USPTO oppositions.'
      ],
      quote: 'Continuous monitoring is essential for maintaining the strength and value of your trademark portfolio.'
    },
    benefits: [
      { icon: 'Eye', title: 'Early Detection', desc: 'Identify potential infringements early when they\'re easiest and cheapest to resolve.' },
      { icon: 'Shield', title: 'Proactive Protection', desc: 'Prevent unauthorized use before it damages your brand reputation and market position.' },
      { icon: 'Globe', title: 'Comprehensive Coverage', desc: 'Monitor USPTO applications, domain registrations, and online marketplace listings.' },
      { icon: 'Bell', title: 'Regular Reports', desc: 'Monthly alerts on potential conflicts with detailed analysis and recommendations.' },
      { icon: 'Scale', title: 'Enforcement Ready', desc: 'Attorney analysis of each potential conflict with enforcement strategy options.' },
      { icon: 'Award', title: 'Brand Strength', desc: 'Consistent enforcement maintains trademark distinctiveness and legal strength.' },
      { icon: 'Target', title: 'Opposition Support', desc: 'File USPTO oppositions against conflicting applications during review period.' },
      { icon: 'Lock', title: 'Portfolio Management', desc: 'Track renewal dates and maintenance requirements for all monitored marks.' }
    ],
    pricing: [
      {
        name: 'Single Mark',
        price: '$299/year',
        description: 'One trademark monitoring',
        features: [
          'USPTO Watch',
          'Monthly Reports',
          'Email Alerts',
          'Basic Analysis',
          'Renewal Reminders'
        ]
      },
      {
        name: 'Portfolio',
        price: '$799/year',
        description: 'Multiple trademark monitoring',
        popular: true,
        features: [
          'Everything in Single Mark',
          'Up to 5 Trademarks',
          'Domain Watch',
          'Online Marketplace Monitoring',
          'Attorney Analysis',
          'Enforcement Guidance',
          'Priority Support'
        ]
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Comprehensive brand protection',
        features: [
          'Everything in Portfolio',
          'Unlimited Trademarks',
          'International Monitoring',
          'Social Media Tracking',
          'Counterfeit Detection',
          'Dedicated Account Manager',
          'Quarterly Strategy Reviews'
        ]
      }
    ],
    process: [
      {
        title: 'Setup and Configuration',
        description: 'Define monitoring parameters, search criteria, and reporting preferences based on your trademark portfolio and brand protection needs.'
      },
      {
        title: 'Continuous Monitoring',
        description: 'Automated surveillance of USPTO filings, domain registrations, and online platforms with weekly scans and immediate alerts for serious threats.'
      },
      {
        title: 'Analysis and Reporting',
        description: 'Monthly reports with attorney analysis of potential conflicts, risk assessment, and recommended enforcement actions for each issue.'
      }
    ]
  },

  'trademark-statement-of-use': {
    title: 'Statement Of Use Filings',
    category: 'Trademark Services',
    tagline: 'Complete your trademark registration process',
    description: 'Expert Statement of Use preparation and filing for intent-to-use applications',
    buttonText: 'File Statement',
    hero: {
      title: 'Statement of Use Filing Services',
      subtitle: 'Complete your intent-to-use trademark registration with proper evidence of use'
    },
    what: {
      title: 'What is a Statement of Use?',
      content: [
        'A Statement of Use (SOU) is a required filing for intent-to-use trademark applications, submitted after you begin using the mark in commerce. The SOU must include specimens showing actual use of the mark with the goods or services listed in your application, along with dates of first use. This filing converts your intent-to-use application into a full trademark registration.',
        'The Statement of Use has strict requirements and deadlines. You must file within 6 months of receiving your Notice of Allowance (with possible extensions up to 3 years). Improper specimens or inaccurate use dates can result in rejection. Our attorneys ensure your SOU is properly prepared with acceptable specimens and accurate declarations.'
      ],
      quote: 'Proper Statement of Use filing is the final crucial step in converting your intent-to-use application into a registered trademark.'
    },
    benefits: [
      { icon: 'CheckCircle2', title: 'Proper Specimens', desc: 'Expert selection and preparation of specimens that meet USPTO acceptance standards.' },
      { icon: 'Calendar', title: 'Deadline Management', desc: 'Track filing deadlines and extension requirements to avoid application abandonment.' },
      { icon: 'FileText', title: 'Accurate Declarations', desc: 'Correct dates of first use in commerce and first use anywhere properly documented.' },
      { icon: 'Shield', title: 'Class-by-Class Filing', desc: 'Strategic filing for different classes based on actual use timing.' },
      { icon: 'Scale', title: 'Legal Compliance', desc: 'Full compliance with USPTO rules and trademark examination guidelines.' },
      { icon: 'Award', title: 'Registration Success', desc: 'Maximize chances of approval and avoid costly corrections or rejections.' },
      { icon: 'Clock', title: 'Extension Strategy', desc: 'Advise on when to file extensions vs. Statement of Use based on business readiness.' },
      { icon: 'Target', title: 'Amendment Options', desc: 'Delete classes or amend descriptions if needed based on actual use.' }
    ],
    pricing: [
      {
        name: 'Single Class',
        price: '$399',
        description: 'One class Statement of Use',
        features: [
          'Specimen Review',
          'Declaration Preparation',
          'Single Class Filing',
          'USPTO Fee Included',
          'Attorney Review'
        ]
      },
      {
        name: 'Multi-Class',
        price: '$699',
        description: 'Multiple class filing',
        popular: true,
        features: [
          'Everything in Single Class',
          'Up to 3 Classes',
          'Multiple Specimen Review',
          'Strategic Class Analysis',
          'Amendment Guidance',
          'Priority Processing'
        ]
      },
      {
        name: 'Extension Package',
        price: '$299/extension',
        description: 'Additional time to file',
        features: [
          'Extension Request Filing',
          '6-Month Additional Time',
          'Up to 5 Extensions Available',
          'Deadline Tracking',
          'Strategic Timing Advice'
        ]
      }
    ],
    process: [
      {
        title: 'Use Assessment',
        description: 'Evaluate your actual use in commerce, review specimens, verify use dates, and determine readiness to file Statement of Use.'
      },
      {
        title: 'SOU Preparation',
        description: 'Prepare Statement of Use with proper specimens, accurate declarations, and strategic class-by-class analysis if needed.'
      },
      {
        title: 'Filing and Follow-Up',
        description: 'File with USPTO, monitor for any examiner questions, address any issues, and track progress to registration.'
      }
    ]
  },

  'trademark-ownership-change': {
    title: 'Trademark Change of Ownership',
    category: 'Trademark Services',
    tagline: 'Transfer trademark rights seamlessly',
    description: 'Professional trademark assignment and ownership transfer services',
    buttonText: 'Transfer Trademark',
    hero: {
      title: 'Trademark Ownership Transfer',
      subtitle: 'Seamless transfer of trademark rights through proper assignment documentation'
    },
    what: {
      title: 'What is Trademark Change of Ownership?',
      content: [
        'A trademark change of ownership, also called an assignment, is the legal transfer of trademark rights from one entity to another. This occurs during business sales, mergers, reorganizations, or gift transfers. Proper assignment requires specific documentation filed with the USPTO to update official ownership records and maintain the trademark\'s validity and enforceability.',
        'Assignment must be handled correctly to avoid losing trademark rights. The assignment document must clearly identify the mark, include proper consideration, contain accurate legal descriptions, and be properly executed by authorized parties. Failure to record the assignment with the USPTO can result in challenges to ownership and difficulty enforcing trademark rights.'
      ],
      quote: 'Proper trademark assignment documentation is essential for maintaining clear chain of title and enforceability of your trademark rights.'
    },
    benefits: [
      { icon: 'Scale', title: 'Legal Validity', desc: 'Attorney-prepared assignment documents ensuring legal validity and enforceability.' },
      { icon: 'FileText', title: 'USPTO Recording', desc: 'Proper recording with USPTO to establish public record of ownership transfer.' },
      { icon: 'Shield', title: 'Rights Protection', desc: 'Maintain full trademark rights and ability to enforce against infringers.' },
      { icon: 'Award', title: 'Chain of Title', desc: 'Clear documentation of ownership history for due diligence and transactions.' },
      { icon: 'CheckCircle2', title: 'Comprehensive Transfer', desc: 'Include goodwill transfer and all associated rights in the assignment.' },
      { icon: 'Globe', title: 'International Coordination', desc: 'Coordinate transfers across multiple jurisdictions when needed.' },
      { icon: 'Clock', title: 'Deadline Compliance', desc: 'Timely filing to avoid gaps in enforcement rights during ownership transition.' },
      { icon: 'Lock', title: 'Confidentiality', desc: 'Protect sensitive business information during the transfer process.' }
    ],
    pricing: [
      {
        name: 'Single Mark',
        price: '$499',
        description: 'One trademark transfer',
        features: [
          'Assignment Document',
          'USPTO Recording',
          'Chain of Title Review',
          'Recordation Certificate',
          'Updated Registration'
        ]
      },
      {
        name: 'Portfolio Transfer',
        price: '$999',
        description: 'Multiple marks transfer',
        popular: true,
        features: [
          'Everything in Single Mark',
          'Up to 5 Trademarks',
          'Bulk Assignment',
          'Comprehensive Documentation',
          'Priority Filing',
          'Due Diligence Support'
        ]
      },
      {
        name: 'Corporate Transaction',
        price: 'Custom',
        description: 'M&A trademark transfer',
        features: [
          'Everything in Portfolio',
          'Unlimited Trademarks',
          'Transaction Support',
          'Due Diligence Review',
          'International Transfers',
          'Post-Closing Services',
          'Dedicated Transaction Team'
        ]
      }
    ],
    process: [
      {
        title: 'Documentation Review',
        description: 'Review transaction documents, verify ownership, identify all marks to be transferred, and gather information from both parties.'
      },
      {
        title: 'Assignment Preparation',
        description: 'Draft assignment document with proper legal descriptions, consideration statements, and authorized signatures from assignor and assignee.'
      },
      {
        title: 'USPTO Recording',
        description: 'File assignment with USPTO, obtain recordation certificate, update trademark records, and provide confirmation to both parties.'
      }
    ]
  },

  'trademark-portfolio': {
    title: 'Trademark Portfolio Management',
    category: 'Trademark Services',
    tagline: 'Maintain and optimize your trademark portfolio',
    description: 'Comprehensive trademark portfolio management and strategic optimization',
    buttonText: 'Manage Portfolio',
    hero: {
      title: 'Trademark Portfolio Management',
      subtitle: 'Strategic management and optimization of your complete trademark portfolio'
    },
    what: {
      title: 'What is Trademark Portfolio Management?',
      content: [
        'Trademark portfolio management is the comprehensive administration of all your trademark assets, including monitoring renewal deadlines, maintaining registrations, tracking enforcement activities, coordinating international filings, and strategically optimizing your portfolio for maximum protection and cost efficiency.',
        'As your business grows, managing multiple trademarks becomes increasingly complex. Missing renewal deadlines can result in loss of rights, while maintaining unused marks wastes resources. Our portfolio management service provides centralized administration, strategic analysis, and proactive maintenance to ensure your trademark portfolio remains strong, enforceable, and aligned with your business objectives.'
      ],
      quote: 'Strategic portfolio management transforms trademark assets from administrative burden into competitive business advantage.'
    },
    benefits: [
      { icon: 'Calendar', title: 'Deadline Management', desc: 'Track and manage all renewal, declaration, and filing deadlines across your portfolio.' },
      { icon: 'Shield', title: 'Rights Maintenance', desc: 'Ensure continued validity and enforceability of all trademark registrations.' },
      { icon: 'Target', title: 'Strategic Optimization', desc: 'Regular portfolio audits to eliminate unused marks and identify coverage gaps.' },
      { icon: 'Globe', title: 'International Coordination', desc: 'Manage trademarks across multiple countries with coordinated renewal strategies.' },
      { icon: 'DollarSign', title: 'Cost Control', desc: 'Optimize portfolio costs while maintaining necessary protection levels.' },
      { icon: 'Award', title: 'Enforcement Tracking', desc: 'Centralized tracking of monitoring alerts and enforcement activities.' },
      { icon: 'FileText', title: 'Comprehensive Reporting', desc: 'Regular reports on portfolio status, upcoming actions, and strategic recommendations.' },
      { icon: 'Lock', title: 'Secure Management', desc: 'Confidential portal access to all trademark documents and portfolio information.' }
    ],
    pricing: [
      {
        name: 'Small Portfolio',
        price: '$599/year',
        description: 'Up to 10 trademarks',
        features: [
          'Deadline Tracking',
          'Renewal Management',
          'Annual Portfolio Review',
          'Document Repository',
          'Email Support'
        ]
      },
      {
        name: 'Growing Business',
        price: '$1,499/year',
        description: 'Up to 25 trademarks',
        popular: true,
        features: [
          'Everything in Small Portfolio',
          'Quarterly Reviews',
          'Strategic Optimization',
          'Monitoring Coordination',
          'International Tracking',
          'Priority Support',
          'Dedicated Portal Access'
        ]
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Unlimited trademarks',
        features: [
          'Everything in Growing Business',
          'Unlimited Trademarks',
          'Monthly Strategic Reviews',
          'Global Portfolio Management',
          'Enforcement Coordination',
          'Dedicated Account Manager',
          'Custom Reporting'
        ]
      }
    ],
    process: [
      {
        title: 'Portfolio Audit',
        description: 'Comprehensive review of all trademark assets, identify gaps and redundancies, establish baseline portfolio status and create management calendar.'
      },
      {
        title: 'Ongoing Administration',
        description: 'Monitor deadlines, coordinate renewals and declarations, track enforcement activities, and provide regular status reports and recommendations.'
      },
      {
        title: 'Strategic Optimization',
        description: 'Regular portfolio reviews, identify cost savings opportunities, recommend new filings for protection gaps, and align portfolio with business strategy.'
      }
    ]
  },

  // COPYRIGHT SERVICES
  'copyright-registration': {
    title: 'Copyright Registration',
    category: 'Copyright Services',
    tagline: 'Register your creative works with the Copyright Office',
    description: 'Professional copyright registration for creative works and intellectual content',
    buttonText: 'Register Copyright',
    hero: {
      title: 'Copyright Registration Services',
      subtitle: 'Protect your creative works with official U.S. Copyright Office registration'
    },
    what: {
      title: 'What is Copyright Registration?',
      content: [
        'Copyright registration establishes a public record of your copyright claim with the U.S. Copyright Office. While copyright protection exists automatically upon creation of original works, registration provides crucial legal benefits including the ability to sue for infringement, eligibility for statutory damages and attorney fees, and strong presumption of validity in court.',
        'Our copyright registration service handles the entire filing process for literary works, musical compositions, artwork, photographs, software code, architectural designs, and other creative content. We ensure proper classification, accurate descriptions, and timely filing to maximize your copyright protection.'
      ],
      quote: 'Copyright registration transforms your automatic rights into enforceable legal protection with powerful remedies against infringement.'
    },
    benefits: [
      { icon: 'Shield', title: 'Legal Presumption', desc: 'Copyright registration creates legal presumption of ownership and validity in court proceedings.' },
      { icon: 'DollarSign', title: 'Statutory Damages', desc: 'Eligibility for statutory damages up to $150,000 per work without proving actual damages.' },
      { icon: 'Scale', title: 'Attorney Fees', desc: 'Recover attorney fees and legal costs from infringers when you prevail in litigation.' },
      { icon: 'FileText', title: 'Public Record', desc: 'Establish official public record of your copyright claim with the U.S. Copyright Office.' },
      { icon: 'Lock', title: 'Customs Protection', desc: 'Register with U.S. Customs to stop importation of infringing copies at the border.' },
      { icon: 'CheckCircle2', title: 'Easy Licensing', desc: 'Simplified licensing and transfer of rights with official registration documentation.' },
      { icon: 'Clock', title: 'Swift Processing', desc: 'Expedited filing options available for time-sensitive copyright protection needs.' },
      { icon: 'Globe', title: 'International Recognition', desc: 'U.S. registration provides basis for international copyright protection in treaty countries.' }
    ],
    pricing: [
      {
        name: 'Single Work',
        price: '$299',
        description: 'One creative work registration',
        features: [
          'Copyright Application',
          'Copyright Office Filing',
          'Electronic Filing',
          'Status Tracking',
          'Registration Certificate'
        ]
      },
      {
        name: 'Collection',
        price: '$499',
        description: 'Multiple related works',
        popular: true,
        features: [
          'Everything in Single Work',
          'Group Registration',
          'Up to 10 Related Works',
          'Expedited Processing',
          'Attorney Review'
        ]
      },
      {
        name: 'Portfolio',
        price: '$999',
        description: 'Large work collection',
        features: [
          'Everything in Collection',
          'Unlimited Works',
          'Multiple Applications',
          'Strategic Filing Plan',
          'Priority Support'
        ]
      }
    ],
    process: [
      {
        title: 'Work Assessment',
        description: 'Review your creative works, determine appropriate copyright classification, and identify the best filing strategy for your needs.'
      },
      {
        title: 'Application Preparation',
        description: 'Prepare copyright application with accurate descriptions, proper classification, and required deposit materials or digital copies.'
      },
      {
        title: 'Filing and Certificate',
        description: 'File with U.S. Copyright Office, monitor application status, address any examiner questions, and deliver registration certificate.'
      }
    ]
  },

  'copyright-enforcement': {
    title: 'Copyright Enforcement',
    category: 'Copyright Services',
    tagline: 'Stop unauthorized use of your copyrighted content',
    description: 'Aggressive copyright enforcement to stop infringement and recover damages',
    buttonText: 'Stop Infringement',
    hero: {
      title: 'Copyright Enforcement Services',
      subtitle: 'Protect your creative works from unauthorized use and copyright infringement'
    },
    what: {
      title: 'What is Copyright Enforcement?',
      content: [
        'Copyright enforcement involves taking legal action against parties who use your copyrighted works without permission. This includes sending cease and desist letters, filing DMCA takedown notices, negotiating settlement agreements, and pursuing litigation when necessary. Effective enforcement protects your creative investments and prevents revenue loss from unauthorized use.',
        'Our enforcement service combines strategic negotiation with aggressive litigation when needed. We handle everything from initial infringement detection through final resolution, whether through settlement or court judgment. Our goal is to stop infringement quickly while maximizing your recovery of damages and legal fees.'
      ],
      quote: 'Swift and strategic copyright enforcement protects your creative investments and deters future infringement.'
    },
    benefits: [
      { icon: 'Shield', title: 'Stop Infringement', desc: 'Immediate action to stop unauthorized use of your copyrighted works through legal demands.' },
      { icon: 'DollarSign', title: 'Damages Recovery', desc: 'Pursue statutory damages, actual damages, and infringer profits through settlement or litigation.' },
      { icon: 'Scale', title: 'Litigation Ready', desc: 'Experienced copyright litigators prepared to take infringers to federal court when necessary.' },
      { icon: 'FileText', title: 'DMCA Takedowns', desc: 'File Digital Millennium Copyright Act takedown notices to remove infringing online content.' },
      { icon: 'Target', title: 'Strategic Approach', desc: 'Evaluate each case for optimal enforcement strategy balancing cost with recovery potential.' },
      { icon: 'Clock', title: 'Fast Action', desc: 'Rapid response to infringement to minimize damage and demonstrate serious enforcement posture.' },
      { icon: 'Globe', title: 'International Reach', desc: 'Pursue infringers across borders with international enforcement partners when needed.' },
      { icon: 'Lock', title: 'Preventive Measures', desc: 'Implement monitoring and prevention strategies to reduce future infringement risk.' }
    ],
    pricing: [
      {
        name: 'Cease & Desist',
        price: '$599',
        description: 'Attorney demand letter',
        features: [
          'Infringement Analysis',
          'Attorney Demand Letter',
          'Negotiation Support',
          'Settlement Documentation',
          '30-Day Follow-Up'
        ]
      },
      {
        name: 'DMCA + Enforcement',
        price: '$999',
        description: 'Comprehensive takedown',
        popular: true,
        features: [
          'Everything in Cease & Desist',
          'DMCA Takedown Notices',
          'Multiple Platform Filing',
          'ISP Communications',
          'Litigation Threat Assessment',
          'Extended Negotiation'
        ]
      },
      {
        name: 'Litigation Package',
        price: 'Custom',
        description: 'Federal court filing',
        features: [
          'Everything in DMCA',
          'Federal Court Complaint',
          'Discovery Management',
          'Expert Witnesses',
          'Trial Preparation',
          'Full Litigation Support'
        ]
      }
    ],
    process: [
      {
        title: 'Infringement Assessment',
        description: 'Analyze the infringement, identify responsible parties, evaluate damages and recovery potential, and develop enforcement strategy.'
      },
      {
        title: 'Demand and Negotiation',
        description: 'Send cease and desist letter or DMCA notice, negotiate settlement terms, draft licensing agreements if applicable.'
      },
      {
        title: 'Litigation if Necessary',
        description: 'File federal court complaint, conduct discovery, pursue judgment or settlement, collect damages awarded by court.'
      }
    ]
  },

  'copyright-assignments': {
    title: 'Copyright Assignments and Licensing',
    category: 'Copyright Services',
    tagline: 'Transfer or license your copyright rights',
    description: 'Professional copyright assignment and licensing agreement preparation',
    buttonText: 'License Copyright',
    hero: {
      title: 'Copyright Assignments & Licensing',
      subtitle: 'Strategic transfer and licensing of copyright rights for creative works'
    },
    what: {
      title: 'What are Copyright Assignments and Licensing?',
      content: [
        'Copyright assignments and licenses are legal agreements that transfer or grant rights to use copyrighted works. An assignment permanently transfers copyright ownership, while a license grants permission to use the work while the creator retains ownership. These agreements are essential for monetizing creative works, enabling collaborations, and structuring business deals involving intellectual property.',
        'Our copyright transaction service drafts comprehensive agreements protecting your interests while facilitating productive business relationships. We handle exclusive and non-exclusive licenses, work-for-hire agreements, assignment contracts, and complex multi-party arrangements. Every agreement includes proper scope definitions, territory restrictions, duration terms, and payment provisions.'
      ],
      quote: 'Well-drafted copyright agreements turn creative assets into revenue streams while protecting your long-term interests.'
    },
    benefits: [
      { icon: 'FileText', title: 'Custom Agreements', desc: 'Attorney-drafted contracts tailored to your specific licensing or assignment needs.' },
      { icon: 'Shield', title: 'Rights Protection', desc: 'Protect retained rights, limit scope of transfers, and preserve future opportunities.' },
      { icon: 'DollarSign', title: 'Revenue Optimization', desc: 'Structure deals for maximum compensation through royalties, advance payments, or lump sums.' },
      { icon: 'Globe', title: 'Territory Control', desc: 'Define geographic scope and exclusive vs. non-exclusive rights in different markets.' },
      { icon: 'Clock', title: 'Duration Terms', desc: 'Set appropriate time periods for licenses with renewal options and termination rights.' },
      { icon: 'Scale', title: 'Legal Compliance', desc: 'Ensure agreements comply with copyright law and are enforceable in court.' },
      { icon: 'Target', title: 'Negotiation Support', desc: 'Attorney guidance through negotiation to achieve favorable terms and protect your position.' },
      { icon: 'Award', title: 'Registration Filing', desc: 'Record assignments with Copyright Office to establish public record of transfers.' }
    ],
    pricing: [
      {
        name: 'Simple License',
        price: '$599',
        description: 'Basic licensing agreement',
        features: [
          'License Agreement',
          'Attorney Drafting',
          'Single Use Case',
          'Basic Terms',
          'Review & Revision'
        ]
      },
      {
        name: 'Comprehensive Agreement',
        price: '$1,299',
        description: 'Complex licensing or assignment',
        popular: true,
        features: [
          'Everything in Simple',
          'Exclusive/Non-Exclusive Options',
          'Royalty Provisions',
          'Territory Restrictions',
          'Termination Clauses',
          'Copyright Office Recording'
        ]
      },
      {
        name: 'Multi-Party Deal',
        price: 'Custom',
        description: 'Complex copyright transactions',
        features: [
          'Everything in Comprehensive',
          'Multiple Parties',
          'Cross-Licensing',
          'Joint Ownership',
          'International Rights',
          'Transaction Support'
        ]
      }
    ],
    process: [
      {
        title: 'Deal Structure',
        description: 'Understand your objectives, evaluate rights to transfer or license, determine optimal deal structure and terms.'
      },
      {
        title: 'Agreement Drafting',
        description: 'Prepare comprehensive copyright agreement with proper scope, payment terms, duration, and protective provisions.'
      },
      {
        title: 'Execution and Recording',
        description: 'Finalize agreement with all parties, obtain signatures, record assignment with Copyright Office if transferring ownership.'
      }
    ]
  },

  // Merge additional services data
  ...additionalServicesData
};
